<template>
  <div class="practice-ops" :class="`practice-ops--${reportShown ? 'report' : view}`" data-test="practice-ops-page">
    <!-- The Wall and the Operatory draw their own top bar (the app shell is hidden on both): no page header there. -->
    <header v-if="showPageHeader" class="ops-head" :class="{ 'ops-head--phone': phoneHeader }">
      <div class="ops-head__titles">
        <h2 class="practice-ops__title" data-test="practice-ops-title">Practice Ops</h2>
        <div v-if="!phoneHeader" class="ops-head__sub" data-test="practice-ops-sub">
          <span class="ops-head__live" aria-hidden="true"></span>
          <span v-if="dateText">{{ dateText }} · live from Open Dental</span>
          <span v-else>Live from Open Dental</span>
        </div>
      </div>

      <div class="ops-head__tools">
        <!-- The four views of one board (and, right after "Wall display", the Wall's Light / Dark switch). A phone-sized window has no picker:
             the phone shows the phone view, and its Wall icon below is the one way out. -->
        <OpsViewPicker
          v-if="!phoneHeader"
          :view="view"
          :items="viewItems"
          show-theme
          :wall-theme="wallThemeValue"
          @select="setView"
          @theme="setWallTheme"
        />
        <!-- Report and Settings: never on the Wall or the Operatory (their own bars have neither). Report is `?view=report` (OpsReportPage). -->
        <button v-if="practicePulsePaidFor && hasReport" type="button" class="ops-head__btn" :class="{ 'ops-head__btn--icon': phoneHeader }" :aria-label="phoneHeader ? 'Report' : null" :title="phoneHeader ? 'Report' : null" data-test="practice-ops-report" @click="openReport">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 3v18h18" /><path d="M7 15v3M12 9v9M17 5v13" /></svg>
          <span v-if="!phoneHeader">Report</span>
        </button>
        <button v-if="phoneHeader" type="button" class="ops-head__btn ops-head__btn--icon" aria-label="Wall display" title="Wall display" data-test="practice-ops-wall-link" @click="setView('wall')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
        </button>
        <button v-if="practicePulsePaidFor" type="button" class="ops-head__btn ops-head__btn--icon" aria-label="Settings" title="Settings" data-test="practice-ops-settings" @click="configurePracticePulse">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
        </button>
      </div>
    </header>

    <!-- The way back to the classic page, one small muted line under the title row. The component decides by role: always shown to a
         Super Admin, shown to an office user only while the area is at "invite" / "default". -->
    <FollowupsUiToggle v-if="showPageHeader" class="practice-ops__toggle" :class="{ 'practice-ops__toggle--phone': phoneHeader }" target="old" page-key="practiceOps" note-text="Super Admin preview — offices still see the classic Practice Pulse." @toggle="$emit('switch-layout', 'old')" />

    <!-- Alert icons need Teamio app 3.0.61+ (this office: x). One quiet line; the board itself works on any version. -->
    <div v-if="alertState.line && showPageHeader" class="ops-note" data-test="practice-ops-alert-line">{{ alertState.line }}</div>

    <div v-if="allSettings && !practicePulsePaidFor" class="ops-state" data-test="practice-ops-locked">
      <h3 class="ops-state__title">Premium feature</h3>
      <p class="ops-state__text" v-html="featureNotEnabledMsg"></p>
    </div>

    <!-- The report: `?view=report` (a page of its own inside Practice Ops, never stored as the view preference; the app shell stays). It draws
         its own header with "Board" to go back, so the page header above is not drawn. An office with no board settings yet still has a
         report (the range report reads the event log, not the board). -->
    <OpsReportPage
      v-else-if="reportShown"
      :site-id="siteID || ''"
      :zone="zone"
      :today-ymd="todayYmd || ''"
      :now-ms="boardNow"
      :room-labels="board.labels"
      :phone="reportPhone"
      @back="closeReport"
    />

    <!-- `loading` is the board listener's first answer: until it arrives "no settings" only means "not loaded yet". -->
    <div v-else-if="allSettings && !practicePulseSettings && !loading" class="ops-state" data-test="practice-ops-needs-config">
      <h3 class="ops-state__title">Set up Practice Ops</h3>
      <p class="ops-state__text">Your settings are not configured yet. Choose your operatories and statuses to start.</p>
      <button type="button" class="ops-state__btn" data-test="practice-ops-configure" @click="configurePracticePulse">Configure now</button>
    </div>

    <template v-else>
      <OpsBoard
        v-if="view === 'board'"
        :board="board"
        :pings="pingModel"
        :zone="zone"
        :now-ms="boardNow"
        :alert-payload="alertPayload"
        :view-settings="viewSettings"
        :show-alert-icons="alertState.showIcons"
        :skeleton="!ppLoaded"
        :stripe-for="stripeFor"
        :text-for="textFor"
        :birthday-for="birthdayFor"
        :provider-for="providerFor"
        :can-move="onMove"
        @move-card="commitMove"
        @ping-card="openPing"
        @send-alert="openPing(null)"
        @alert-click="openChecklist"
        @dismiss-card="dismissCard"
        @clear-ping="clearPing"
        @drag-start="onDragBegin"
        @drag-end="onDragFinish"
      />
      <!-- The iPad pinned to one room, full screen (?view=operatory hides the shell, like the Wall). Every move is ONE event, decided by
           requestMove (the Board's own rules). -->
      <OpsOperatory
        v-else-if="view === 'operatory'"
        :board="board"
        :zone="zone"
        :now-ms="boardNow"
        :alert-payload="alertPayload"
        :view-settings="viewSettings"
        :show-alert-icons="alertState.showIcons"
        :skeleton="!ppLoaded"
        :stripe-for="stripeFor"
        :text-for="textFor"
        :birthday-for="birthdayFor"
        :provider-for="providerTitleFor"
        :can-move="onMove"
        @request-move="requestMove"
        @ping-card="openPing"
        @ping-room="openPingFor"
        @alert-click="openChecklist"
        @dismiss-card="dismissCard"
        @drag-start="onDragBegin"
        @drag-end="onDragFinish"
      >
        <!-- Its own top bar (title, pinned room, clock) ends in the SAME view picker: Board / Phone navigate back to the shell. -->
        <template #picker><OpsViewPicker :view="view" :items="viewItems" @select="setView" /></template>
      </OpsOperatory>

      <!-- The phone: tabs, no drag, a Move-to sheet. The sheet's choice is the same request a drop makes. -->
      <OpsPhone
        v-else-if="view === 'phone'"
        :board="board"
        :pings="pingModel"
        :zone="zone"
        :now-ms="boardNow"
        :alert-payload="alertPayload"
        :view-settings="viewSettings"
        :show-alert-icons="alertState.showIcons"
        :skeleton="!ppLoaded"
        :stripe-for="stripeFor"
        :text-for="textFor"
        :birthday-for="birthdayFor"
        :provider-for="providerFor"
        @request-move="requestMove"
        @ping-card="openPing"
        @send-alert="openPing(null)"
        @alert-click="openChecklist"
        @dismiss-card="dismissCard"
        @clear-ping="clearPing"
      />

      <!-- The TV: nothing to press, no chat, no note. The shell is hidden by Layout.vue (?view=wall). -->
      <OpsWall
        v-else
        :board="board"
        :pings="pingModel"
        :theme="wallThemeValue"
        :site-name="siteName"
        :date-text="dateText"
        :zone="zone"
        :now-ms="boardNow"
        :alert-payload="alertPayload"
        :view-settings="viewSettings"
        :show-alert-icons="alertState.showIcons"
        :skeleton="!ppLoaded"
        :stripe-for="stripeFor"
        :text-for="textFor"
        :birthday-for="birthdayFor"
        :provider-for="providerFor"
        @toggle-theme="toggleWallTheme"
        @exit="exitWall"
      />

      <!-- Team chat stays embedded on the Board, as on the classic page. -->
      <div v-if="showChat && view === 'board'" class="ops-chat" data-test="practice-ops-chat">
        <chat-container mode="embedded" :current-user-id="userID" />
      </div>
    </template>

    <!-- The visit checklist for the tapped alert icon (Schedule View's own card). -->
    <OpsChecklistPopover
      v-if="checklistView"
      :appt="checklistView.appt"
      :categories="checklistView.categories"
      :checked="checklistView.checked"
      :anchor="checklist.anchor"
      :focus-category="checklist.focus"
      :accent="checklistView.accent"
      :op-name="checklistView.opName"
      :provider-name="checklistView.providerName"
      :status-text="checklistView.statusText"
      :pending="!alertPayload"
      :incomplete="alertsIncomplete"
      @toggle="onChecklistToggle"
      @open-tab="onChecklistOpenTab"
      @close="closeChecklist"
    />

    <!-- Send alert. The inherited startPing / startPingWithShortcut do the work; this only draws the dialog. -->
    <b-modal ref="ping" id="ping" title="Send alert" body-class="bg-white" data-test="practice-ops-send-alert">
      <div class="ops-send">
        <label class="ops-send__label" for="ops-send-room">Room</label>
        <select id="ops-send-room" v-model="pingRoom" class="form-control ops-send__room" data-test="ops-send-room">
          <option v-for="room in board.rooms" :key="room.opNum" :value="room.opNum">{{ room.label }}</option>
        </select>

        <div v-if="alertsGroupByEmployee" class="ops-send__quick">
          <div v-for="(value, key) in alertsGroupByEmployee" :key="`quick-${key}`">
            <span class="ops-send__label">{{ key }}</span>
            <div class="ops-send__chips">
              <button
                v-for="(alert, index) in value"
                :key="`quick-${key}-${index}`"
                type="button"
                class="btn btn-success btn-sm"
                @click="startPingWithShortcut(alert)"
              >{{ alert.alertText }}</button>
            </div>
          </div>
        </div>

        <p v-if="alertsShortcutsAvailable" class="ops-send__or">OR</p>

        <label class="ops-send__label">Send to</label>
        <multiselect
          v-if="activeEmployees"
          v-model="selectedEmployee"
          :options="activeEmployees"
          :multiple="false"
          :taggable="false"
          :show-labels="false"
          :custom-label="employeeCustomLabel"
          track-by="EmployeeID"
          placeholder="Select a team member"
        />

        <label class="ops-send__label" for="ops-send-msg">Message</label>
        <textarea id="ops-send-msg" v-model="msg" rows="4" class="form-control" placeholder="Like the tooth or what you need" ref="msg"></textarea>
      </div>

      <div slot="modal-footer" class="w-100">
        <button type="button" class="btn btn-danger btn-sm float-right" @click="hideModal('ping')">Cancel</button>
        <button type="button" class="btn btn-success btn-sm float-right mr-3" data-test="ops-send-submit" @click="startPing">Send</button>
      </div>
    </b-modal>

    <!-- The ONE <slideout-panel> outlet of this page (Configure settings, Check-out notes and the details panels
         open through $showPanel). The classic template is not rendered here, so its outlet is not mounted. -->
    <slideout-panel></slideout-panel>
  </div>
</template>

<script>
/**
 * Practice Ops — the redesigned Practice Pulse board (plan docs/plans/2026-10-30-practice-ops-plan.md §6).
 *
 * Phase 4: the page shell, the patient card and the four views — Board (chunk B), Operatory, Phone and Wall (chunk C), then the Settings
 * panel (OpsSettingsPanel), the Check-out panel (OpsCheckoutPanel) and the Report link in the header (chunk D).
 *
 * THE VIEWS. `?view=` → the stored choice → the screen width (viewPref.js resolveView). The Wall is reached ONLY through the URL
 * (`?view=wall`): Layout.vue hides the app shell on exactly that query, so a stored "wall" is ignored here (it would draw the Wall
 * inside the shell) and the picker's "Wall display" navigates instead of just switching. `?theme=` / the stored theme pick the Wall's
 * light or dark (live toggle in its corner). EVERY move — a Board drop, the Operatory's Seat next / Check out, the Phone's Move-to
 * sheet — ends in requestMove → moves.js planMove (the one-patient-per-room refusal + toast) → commitMove → the classic moveCard.
 *
 * THE REPORT (Phase 5) is `?view=report`: OpsReportPage in place of the board, the app shell kept, its own header with "Board" to go back.
 * It is a destination, not a fifth view: report.js isReportQuery reads the URL, viewPref.js VIEWS stays four, and nothing is stored. The
 * header's Report button and the phone's Report icon both navigate there (openReport); the classic report (ShowPracticePulseDetails)
 * stays on the classic page only. The page passes it what only the page knows: the practice, its zone and "today", the corrected
 * clock, and the rooms' Practice Ops names (the report carries only OperatoryNum).
 *
 * `extends` the classic PracticePulse.vue, which is the fallback and holds the store wiring, the Firestore
 * listener, ping / sound / push, chat and the Open Dental calls — fix that logic ONCE THERE. This file owns the
 * template and the things the new board adds. Vue merges the parent's data, computeds, methods and watchers, and
 * RUNS the parent's created / mounted / beforeDestroy hooks before this component's own. What that means here:
 *
 *   • the inherited created() opens the Firestore board listener ONCE (init → getFbOps). Never call init / getFbOps
 *     again: a second onSnapshot would be a second listener on the same document;
 *   • the inherited `practicePulse` watcher still rebuilds the classic `pp` model (`pp.ops` / `pp.alerts`), and arms
 *     a 1 s interval per Ready patient and calls setAlertTimers. Both go through methods this file overrides as
 *     NO-OPS (updatePatientTime, setAlertTimers): waits are computed from timestamps and the clock below, never
 *     counted up. The intervals it still creates are the parent's own and are cleared by the parent's beforeDestroy;
 *   • the inherited `practicePulse.alerts` watcher plays the ping sound and the browser notification — kept as is;
 *   • the inherited mounted() starts a 30 s `nowTick` interval. It is replaced here by a minute-boundary tick.
 *
 * THE BOARD MODEL is buildBoard(<the document's settings + the classic `pp.ops`>). `pp.ops` (not the raw Firestore
 * snapshot) so a drop shows at once: the classic page already guards it against stale snapshots (pendingDragOperation,
 * window.__isDragging) and a move is applied to it locally before the write, exactly as handleMoveFromCheckin does.
 *
 * THE CLOCK. `boardNow` = the device clock + a server offset. The page re-renders at the START of every minute
 * (clock.js msToNextMinute), at once when the tab becomes visible, and re-reads the offset on load and when the browser
 * comes back online (HEAD /index.html: `Date` + `Age`; an offset beyond ±6 h is a cached response and counts as 0).
 * Every wait is computed from stored timestamps and `boardNow` — a laptop that slept shows the right minutes at once.
 *
 * THE ICONS are Schedule View's: scheduleView/fetchAlerts for the practice's today (on load, and again — debounced 3 s —
 * when the set of patients on the board changes or an `apptChanged` signal arrives), the store's live task listener when the
 * office's `task` switch is on. An office without Schedule View access gets no icons and one line saying why (alertGate.js).
 *
 * LIVE NOTE / PROCEDURES (§4b): one POST /get-practice-pulse-ops for every AptNum on the board, then one debounced refetch
 * for the AptNums an `apptChanged` signal names (apptChanges.js). The card prefers them; the document's copy is the first paint.
 */
import Vue from 'vue';
import { mapActions } from 'vuex';
import PracticePulse from '../PracticePulse.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import OpsBoard from './OpsBoard.vue';
import OpsOperatory from './OpsOperatory.vue';
import OpsPhone from './OpsPhone.vue';
import OpsWall from './OpsWall.vue';
import OpsViewPicker from './OpsViewPicker.vue';
import OpsReportPage from './OpsReportPage.vue';
import OpsChecklistPopover from './OpsChecklistPopover.vue';
import OpsSettingsPanel from './OpsSettingsPanel.vue';
import OpsCheckoutPanel from './OpsCheckoutPanel.vue';
import { buildBoard, tsToMs } from '@/utils/practiceOps/board';
import { chatOn } from '@/utils/practiceOps/settingsModel';
import { cardLastFirst, planMove } from '@/utils/practiceOps/moves';
import {
  resolveView, readView, writeView, wallTheme, readWallTheme, writeWallTheme, PHONE_MAX_WIDTH,
} from '@/utils/practiceOps/viewPref';
import { FULL_SCREEN_VIEWS } from '@/utils/practiceOps/wallShell';
import { msToNextMinute, serverOffsetMs, plausibleOffsetMs } from '@/utils/practiceOps/clock';
import { changedAptNums } from '@/utils/practiceOps/apptChanges';
import { alertIconState } from '@/utils/practiceOps/alertGate';
import { isReportQuery, REPORT_VIEW } from '@/utils/practiceOps/report';
import { siteLocalDate } from '@/utils/practiceOps/siteDate';
import { officeViewSettings, alertSwitchOn } from '@/utils/practiceOps/viewSettings';
import {
  boardAptNums, boardPatNums, missingAptNums, chunkAptNums, detailsFromReply, liveCardText,
} from '@/utils/practiceOps/apptDetails';
import {
  buildPings, dateLine, localDateTimeText, providerLabel,
} from '@/utils/practiceOps/boardText';
import { browserStorage } from '@/utils/followups/uiVersion';
import { odColorToHex } from '@/utils/scheduleView/provColor';
import { ALERT_KEYS } from '@/utils/scheduleView/settingsForm';
import {
  blockAlertKeys, alertsForPatient, describeAlert, hasAlertErrors,
} from '@/utils/scheduleView/alerts';
import { checklistsForDay, ALERT_CATEGORY, checkKey } from '@/utils/scheduleView/checklist';
import { visitAppointment } from '@/utils/scheduleView/visitAppt';
import { appointmentNote } from '@/utils/scheduleView/apptNote';

/** A burst of changes (a signal per edited field, an arrival) becomes ONE request. */
const DETAILS_DEBOUNCE_MS = 1000;
const ALERT_DEBOUNCE_MS = 3000;
/** The release delay of the "a card is moving" flag — the classic page's own number (onEnd). */
const DRAG_RELEASE_MS = 1500;

// The design's order: the Light / Dark switch sits right after "Wall display" (OpsViewPicker).
const VIEW_ITEMS = [
  { id: 'board', label: 'Board' },
  { id: 'wall', label: 'Wall display' },
  { id: 'operatory', label: 'Operatory' },
  { id: 'phone', label: 'Phone' },
];

const STATUS_LABEL = {
  arrived: 'Arrived', ready: 'Ready', inTreatment: 'In treatment', dismissed: 'Dismissed',
};

export default {
  name: 'PracticeOpsPage',

  extends: PracticePulse,

  components: {
    FollowupsUiToggle, OpsViewPicker, OpsReportPage, OpsBoard, OpsOperatory, OpsPhone, OpsWall, OpsChecklistPopover,
  },

  data() {
    return {
      viewItems: VIEW_ITEMS,
      /** 'board' | 'wall' | 'operatory' | 'phone' — set in created() from ?view=, the stored choice, then the width. */
      view: 'board',
      /** 'light' | 'dark' — the Wall's theme (?theme=, then the stored choice, then light). */
      wallThemeValue: 'light',
      /** The window's width (CSS px): a phone-sized window gets the compact phone header. */
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
      /** The device clock at the last tick; boardNow adds the server offset. */
      nowMs: Date.now(),
      serverOffset: 0,
      /** AptNum → { AptNum, PatNum, Note, ProcDescript, ProvNum, Op, Pattern, AptDateTime } from /get-practice-pulse-ops. */
      apptDetails: {},
      /** The open visit checklist: { aptNum, patNum, anchor, focus } or null. */
      checklist: null,
      /** The Send alert dialog's room (an OperatoryNum) — the inherited startPing reads selectedOpID / selectedOpName. */
      pingRoom: null,
    };
  },

  computed: {
    isWall() {
      return this.view === 'wall';
    },

    /** The Wall and the Operatory are screens of their own: the app shell is hidden on both (Layout.vue, by the URL). */
    isFullScreen() {
      return FULL_SCREEN_VIEWS.indexOf(this.view) !== -1;
    },

    /** The board itself is drawn (not the "Premium feature" / "Set up Practice Ops" states, which have no top bar of their own). */
    boardDrawn() {
      if (this.allSettings && !this.practicePulsePaidFor) return false;
      return !(this.allSettings && !this.practicePulseSettings && !this.loading);
    },

    /** `?view=report` — the report page. A view of its own that is NEVER stored as the view preference (viewPref.js VIEWS has four). */
    reportOpen() {
      return isReportQuery(this.$route && this.$route.query);
    },

    /** The report is drawn: asked for, and the office has Practice Ops at all (an unpaid office sees the locked message, as everywhere). */
    reportShown() {
      return this.reportOpen && !(this.allSettings && !this.practicePulsePaidFor);
    },

    /** A phone-sized window: the report draws a card per visit instead of its wide table. */
    reportPhone() {
      return this.viewportWidth > 0 && this.viewportWidth < PHONE_MAX_WIDTH;
    },

    /** The page's title row: not on a full-screen view that draws its own bar, nor on the report (its own header) — but the states above keep it, so there is always a way out. */
    showPageHeader() {
      return !(this.isFullScreen && this.boardDrawn) && !this.reportShown;
    },

    /** The Phone view on a phone-sized window: title + icon buttons, no picker (design "Phone — Check-in"). */
    phoneHeader() {
      return this.view === 'phone' && this.viewportWidth > 0 && this.viewportWidth < PHONE_MAX_WIDTH;
    },

    /** The practice's name for the Wall's header ('' until the sites load). */
    siteName() {
      try {
        const site = typeof this.getSite === 'function' ? this.getSite(this.siteID) : null;
        return site && typeof site.Name === 'string' ? site.Name.trim() : '';
      } catch (e) {
        return '';
      }
    },

    /** The practice's IANA time zone (GENERAL_TIMEZONE), '' until the settings load. */
    zone() {
      const row = this.allSettings && this.allSettings.GENERAL_TIMEZONE;
      return row && typeof row.PropertyValue === 'string' ? row.PropertyValue.trim() : '';
    },

    boardNow() {
      return this.nowMs + this.serverOffset;
    },

    dateText() {
      return dateLine(this.boardNow, this.zone);
    },

    todayYmd() {
      return siteLocalDate(this.boardNow, this.zone);
    },

    /** The board as the views draw it. See the header for why `pp.ops` and not the raw snapshot. */
    board() {
      const doc = this.practicePulse || {};
      return buildBoard(
        { settings: doc.settings, allOps: doc.allOps, ops: this.pp ? this.pp.ops : null },
        { now: this.boardNow, nameOf: this.nameOf }
      );
    },

    boardPatNumsKey() {
      return boardPatNums(this.board).join(',');
    },

    boardAptNumsKey() {
      return boardAptNums(this.board).join(',');
    },

    /** Team chat on the Board: the Settings panel's "Show team chat on the Board" (`settings.showChat`; absent => OFF, as classic). */
    showChat() {
      return !!(this.userID && chatOn(this.practicePulseSettings));
    },

    /** The report link shows once the board has columns, as the classic page's does. */
    hasReport() {
      return !!(this.pp && this.pp.ops && Object.keys(this.pp.ops).length > 0);
    },

    // ── alert icons (Schedule View's) ────────────────────────────────────────────────────────
    svState() {
      return (this.$store && this.$store.state && this.$store.state.scheduleView) || null;
    },

    alertState() {
      return alertIconState(this.svState ? this.svState.accessRefusal : null);
    },

    /** The day's /alerts payload with the live task counts folded in — the store's own getter. */
    alertPayload() {
      const getters = this.$store && this.$store.getters;
      return (getters && getters['scheduleView/alertsForDate']) || null;
    },

    alertsIncomplete() {
      return hasAlertErrors(this.alertPayload);
    },

    /** The office's Schedule View switches, read from the settings already loaded (see utils/practiceOps/viewSettings.js). */
    viewSettings() {
      return officeViewSettings(this.settings);
    },

    // ── the Alerts column ────────────────────────────────────────────────────────────────────
    pingModel() {
      const alerts = this.pp && Array.isArray(this.pp.alerts) ? this.pp.alerts : [];
      const providerSettings = (this.practicePulseSettings && this.practicePulseSettings.providerSettings) || {};
      const labels = this.board.labels || {};
      const rows = alerts.map((a, index) => ({
        index,
        createdMs: tsToMs(a && a.createdAt),
        to: a ? a.pingToEmployeeID : null,
        from: a ? a.pingFromEmployeeID : null,
        opName: a ? a.opName : '',
        opLabel: a && labels[a.opID] ? labels[a.opID] : '',
        msg: a ? a.msg : '',
      }));
      return buildPings(rows, {
        nowMs: this.boardNow,
        thresholdMin: this.board.thresholdMin,
        tz: this.zone,
        toName: id => (id ? this.employeeName(id) : ''),
        fromName: id => (id ? this.employeeName(id) : ''),
        colorOf: (id) => {
          const entry = providerSettings[id];
          return entry && /^#[0-9a-f]{6}$/i.test(entry.color || '') ? entry.color : null;
        },
      });
    },

    // ── the visit checklist ──────────────────────────────────────────────────────────────────
    /** The card the open checklist belongs to, looked up live; null once it has left the board. */
    openCard() {
      if (!this.checklist) return null;
      const all = [].concat(this.board.checkin, this.board.checkout);
      this.board.rooms.forEach((r) => { r.cards.forEach(c => all.push(c)); });
      return all.find(c => c.patNum === this.checklist.patNum && c.aptNum === this.checklist.aptNum) || null;
    },

    /** Everything the card needs — appt, categories, ticks — or null while nothing is open. */
    checklistView() {
      const card = this.openCard;
      if (!card) return null;
      const text = this.textFor(card);
      const appt = visitAppointment({
        aptNum: card.aptNum,
        patNum: card.patNum,
        first: card.first,
        last: card.last,
        detail: this.apptDetails[card.aptNum] || null,
        scheduledText: localDateTimeText(card.scheduledMs, this.zone),
        procDescript: text.proc,
        note: text.note,
        opNum: card.opNum,
        provNum: card.provNum,
      });
      const getters = this.$store.getters;
      const checks = (this.svState && this.svState.checks) || {};
      const built = checklistsForDay({
        appointments: [{ AptNum: card.aptNum, PatNum: card.patNum }],
        payload: this.alertPayload,
        viewSettings: this.viewSettings,
        order: ALERT_KEYS,
        tasksByPatNum: getters['scheduleView/taskLinesByPatNum'] || {},
        checks,
        today: this.todayYmd,
        deps: { blockAlertKeys, alertsForPatient, describe: describeAlert },
      });
      const entry = built[card.aptNum] || { categories: [] };
      const checked = {};
      entry.categories.forEach((c) => {
        c.items.forEach((it) => {
          if (checks[checkKey(card.aptNum, it.id)]) checked[it.id] = true;
        });
      });
      const roomNum = card.opNum || card.lastRoom;
      return {
        appt,
        categories: entry.categories,
        checked,
        accent: this.stripeFor(card) || undefined,
        opName: (roomNum && this.board.labels[roomNum]) || '',
        providerName: this.providerFor(card),
        statusText: STATUS_LABEL[card.status] || '',
      };
    },
  },

  watch: {
    /** A card the open checklist belonged to left the board (a move, a refresh): the popover goes with it. */
    openCard(card) {
      if (this.checklist && !card) this.closeChecklist();
    },

    /** Who is on the board changed: follow them with the live task listener, and (debounced) refresh the alerts. */
    boardPatNumsKey(current, prev) {
      this.syncTaskWatch();
      if (!this.alertsRequested) this.loadAlerts();
      else if (prev !== '') this.queueAlertRefresh();
    },

    /** An appointment appeared on the board: ask for its live details (once; changes come through apptChanged). */
    boardAptNumsKey() {
      this.syncApptDetails();
    },

    /** The first snapshot is the baseline for `apptChanged`: signals already there are not "changes". */
    ppLoaded(loaded) {
      if (loaded && this.apptBaseline === null) this.adoptApptBaseline();
    },

    /** The nightly reset clears the map; a changed value is a changed appointment. */
    'practicePulse.apptChanged': function onApptChanged(next) {
      const map = next && typeof next === 'object' ? next : {};
      if (this.apptBaseline === null) { this.apptBaseline = map; return; }
      const changed = changedAptNums(this.apptBaseline, map);
      this.apptBaseline = map;
      if (changed.length) {
        this.queueApptRefetch(changed);
        this.queueAlertRefresh();
      }
    },

    /** The Send alert dialog's room select: the inherited startPing reads selectedOpID / selectedOpName. */
    pingRoom(opNum) {
      this.applyPingRoom(opNum);
    },

    /** The icons follow the office's task switch, and stop when the office cannot have icons. */
    'alertState.showIcons': function onShowIcons() {
      this.syncTaskWatch();
    },
  },

  created() {
    // Plain fields, not reactive state: bookkeeping that must not re-render the page.
    this.minuteTimer = null;
    this.alertTimer = null;
    this.apptTimer = null;
    this.releaseTimer = null;
    this.pendingApptNums = {};
    this.detailsAsked = {};
    this.apptBaseline = null;
    this.alertsRequested = false;
    this.taskWatching = false;
    this.isGone = false;

    const query = (this.$route && this.$route.query) || {};
    const storage = browserStorage();
    // A stored "wall" or "operatory" is never honoured: both are full-screen views and need `?view=<id>` in the URL (Layout.vue hides the
    // shell on exactly that query). Honouring a stored one would draw it INSIDE the shell; redirecting to the query instead would strand a
    // desktop user on a shell-less page that the sidebar link (which has no query) keeps sending them back to. So a bare URL falls back to
    // the width default: the iPad's bookmark carries `?view=operatory`, and the room it is pinned to is remembered separately.
    const stored = readView(storage);
    this.view = resolveView({ query, stored: FULL_SCREEN_VIEWS.indexOf(stored) !== -1 ? null : stored, width: typeof window !== 'undefined' ? window.innerWidth : 0 });
    this.wallThemeValue = wallTheme(readWallTheme(storage), query);
  },

  mounted() {
    // The inherited mounted() started a 30 s tick; the board ticks at the start of each minute instead.
    if (this.nowTickIntervalId) {
      this.removeIntervalId(this.nowTickIntervalId);
      this.nowTickIntervalId = null;
    }

    this.nowMs = Date.now();
    this.scheduleMinuteTick();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onOnline);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.fetchServerOffset();
    if (this.ppLoaded) this.adoptApptBaseline();
    this.loadAlerts();
    this.syncTaskWatch();
    this.syncApptDetails();
  },

  beforeDestroy() {
    this.isGone = true;
    [this.minuteTimer, this.alertTimer, this.apptTimer, this.releaseTimer].forEach((t) => { if (t) clearTimeout(t); });
    this.minuteTimer = null;
    this.alertTimer = null;
    this.apptTimer = null;
    this.releaseTimer = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('resize', this.onResize);
    // A card in the air when the page goes must not leave the store frozen for the next page.
    window.__isDragging = false;
    this.pendingDragOperation = null;
    if (this.taskWatching && this.$store) this.$store.dispatch('scheduleView/unwatchTasks');
    this.taskWatching = false;
  },

  methods: {
    ...mapActions('practicepulse', ['getApptDetails']),

    // ── overrides of the classic page ───────────────────────────────────────────────────────
    /** NO READY GATE here (plan behaviour call 2): anyone in Check-in can be seated. The classic page keeps its own. */
    onMove() {
      return true;
    },

    /**
     * Settings -> the redesigned panel (OpsSettingsPanel): Statuses, Operatories, Alerts, Check-out. It saves through the store to
     * the SAME place classic does (the board document's `settings`), only the keys the office changed. The "Configure now" button of
     * the not-set-up state comes here too. Backdrop and Esc do not close it (a click away must not throw edits away).
     */
    configurePracticePulse(tab) {
      try {
        this.$showPanel({
          component: OpsSettingsPanel,
          openOn: 'right',
          width: 640,
          cssClass: 'ops-sp-slideout',
          disableBgClick: true,
          disableEscClick: true,
          props: {
            stored: this.practicePulseSettings || null,
            docAllOps: (this.practicePulse && Array.isArray(this.practicePulse.allOps)) ? this.practicePulse.allOps : [],
            initialTab: typeof tab === 'string' ? tab : 'statuses',
          },
        });
      } catch (err) {
        console.log(err);
      }
    },

    /**
     * The name the Check-out panel shows: the roster's (first name only while privacy is on, read NOW — the card's own cached `name`
     * was fixed the first time it was drawn), else the card's, else the PatNum.
     */
    checkoutNameFor(item) {
      const patient = item ? this.getPatient(item.patNum) : null;
      if (patient) return this.privacyOn ? `${patient.FName || ''}`.trim() : `${patient.FName || ''} ${patient.LName || ''}`.trim();
      if (item && item.name) return item.name;
      return item && item.patNum ? `Patient #${item.patNum}` : '';
    },

    /**
     * Check-out -> the redesigned panel (OpsCheckoutPanel, which extends the classic CheckoutNotes and keeps its load / save logic).
     * The flow after it is the classic one, unchanged: Save -> checkout(); Cancel on a card that was DRAGGED here -> cancelCheckout().
     */
    checkoutNotes(moveFromOpID, item, index, cancelNeeded) {
      const _this = this;
      try {
        const name = this.checkoutNameFor(item);
        let cancelNote = '';
        if (cancelNeeded) {
          const room = moveFromOpID === 'checkin' ? 'Check-in' : (this.board.labels[moveFromOpID] || 'its room');
          cancelNote = `Cancel moves ${name || 'this patient'} back to ${room}.`;
        }
        const checkoutNotesHandle = this.$showPanel({
          component: OpsCheckoutPanel,
          openOn: 'right',
          width: 600,
          cssClass: 'ops-co-slideout',
          disableBgClick: true,
          disableEscClick: true,
          props: {
            aptNum: item && item.aptNum ? item.aptNum : this.selectedAptNum,
            patientNum: item && item.patNum ? item.patNum : this.selectedPatNum,
            patientName: item ? name : this.selectedPatientName,
            ppID: this.ppID,
            practicePulseSettings: this.practicePulseSettings,
            patientItem: item,
            cancelNote,
          },
        });

        checkoutNotesHandle.promise.then(async (result) => {
          // if this was called from checkout already then we don't need to do anything
          if (moveFromOpID !== 'checkout') {
            if (result) {
              _this.ppID = result.ppID;
              if (result.checkout) {
                _this.checkout(item, moveFromOpID, index, cancelNeeded);
              } else if (cancelNeeded) {
                _this.cancelCheckout(moveFromOpID, item, index);
              }
            }
          }
        });
      } catch (err) {
        console.log(err);
      }
    },

    /**
     * Report -> the report page: the same page, `?view=report` (other params kept; the route is keyed on its full path, so the page remounts
     * into it). It is a destination, not a view preference: nothing is stored, and the app shell stays. The classic report
     * (ShowPracticePulseDetails) is reachable from the classic page only.
     */
    openReport() {
      this.goToView(REPORT_VIEW);
    },

    /** "Board" on the report: back to the board (or the phone view) this window resolves to, the shell untouched. */
    closeReport() {
      this.goToView(this.view);
    },

    /** Waits are computed from timestamps and the minute clock — never counted up by a per-patient interval. */
    updatePatientTime() {},

    /** The Alerts column's elapsed chips come from the same clock; the classic 1 s alert timers are not used. */
    setAlertTimers() {},

    // ── the views ───────────────────────────────────────────────────────────────────────────
    /**
     * The picker's choice. The shell hides on `?view=wall` / `?view=operatory` (Layout.vue reads the URL), so a view change that
     * touches a full-screen view — going TO one, or leaving one — NAVIGATES (the page remounts into the new view, and the shell comes
     * back or goes away with it). Board <-> Phone stays in place. A full-screen view is never stored (see created()); Board / Phone are.
     */
    setView(id) {
      if (!this.viewItems.some(item => item.id === id)) return;
      const toFullScreen = FULL_SCREEN_VIEWS.indexOf(id) !== -1;
      if (!toFullScreen) writeView(browserStorage(), id);
      if (toFullScreen || this.isFullScreen) {
        this.goToView(id);
        return;
      }
      this.view = id;
    },

    /** Same page, `?view=<id>` (other params kept). The route is keyed on its full path, so the page remounts into the new view. */
    goToView(id) {
      if (!this.$router || typeof this.$router.push !== 'function') return;
      const query = Object.assign({}, (this.$route && this.$route.query) || {}, { view: id });
      const result = this.$router.push({ query });
      if (result && typeof result.catch === 'function') result.catch(() => {});
    },

    /** The Wall's small "Exit wall display" link: back to the Board (which brings the shell back). */
    exitWall() {
      this.goToView('board');
    },

    setWallTheme(id) {
      this.wallThemeValue = id;
      writeWallTheme(browserStorage(), id);
    },

    /** The Wall's corner sun / moon: flips the theme live (no reload) and remembers it on this device. */
    toggleWallTheme() {
      this.setWallTheme(this.wallThemeValue === 'dark' ? 'light' : 'dark');
    },

    // ── the clock ───────────────────────────────────────────────────────────────────────────
    scheduleMinuteTick() {
      if (this.isGone) return;
      if (this.minuteTimer) clearTimeout(this.minuteTimer);
      this.minuteTimer = setTimeout(() => {
        this.minuteTimer = null;
        this.nowMs = Date.now();
        this.scheduleMinuteTick();
      }, msToNextMinute(Date.now() + this.serverOffset));
    },

    /** The window changed size: the phone header follows the width. */
    onResize() {
      this.viewportWidth = window.innerWidth;
    },

    /** The tab is back (or the screen woke): redraw at once and re-aim the tick — a hidden tab's timers run late. */
    onVisibilityChange() {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.nowMs = Date.now();
        this.scheduleMinuteTick();
      }
    },

    onOnline() {
      this.fetchServerOffset();
    },

    /** server − device, from HEAD /index.html (never a cache hit; `Date` + `Age` anyway). ±6 h or more counts as 0. */
    async fetchServerOffset() {
      if (typeof fetch !== 'function') return;
      const start = Date.now();
      try {
        const response = await fetch('/index.html', { method: 'HEAD', cache: 'no-store' });
        const end = Date.now();
        if (this.isGone || !response || !response.headers) return;
        const offset = plausibleOffsetMs(serverOffsetMs({
          dateHeader: response.headers.get('Date'),
          ageHeader: response.headers.get('Age'),
          requestStartMs: start,
          responseEndMs: end,
        }));
        this.serverOffset = offset;
        this.nowMs = Date.now();
        this.scheduleMinuteTick();
      } catch (e) {
        // offline or blocked: keep the last offset
      }
    },

    // ── names, colours, text for the cards ──────────────────────────────────────────────────
    /** The same roster lookup as the classic page; a miss falls through to "Patient #<PatNum>". Privacy shows first names only. */
    nameOf(card) {
      const patient = this.getPatient(card.patNum);
      if (!patient) return null;
      return this.privacyOn ? { first: patient.FName, last: '' } : { first: patient.FName, last: patient.LName };
    },

    providerFor(card) {
      const list = Array.isArray(this.providers) ? this.providers : [];
      const p = list.find(x => x && x.ProvNum === card.provNum);
      return p ? `${p.FName || ''} ${p.LName || ''}`.trim() : '';
    },

    /** "Dr. Kapoor" for a doctor, else the same name as providerFor — the Operatory view's facts line. */
    providerTitleFor(card) {
      const list = Array.isArray(this.providers) ? this.providers : [];
      const p = list.find(x => x && x.ProvNum === card.provNum);
      return p ? providerLabel(p) : '';
    },

    /** The provider's colour: the office's own choice for that person (providerSettings), else Open Dental's. */
    stripeFor(card) {
      let own = null;
      try {
        const employeeID = this.getEmployeeIDFromProvNum(card.provNum);
        const settings = this.practicePulseSettings && this.practicePulseSettings.providerSettings;
        own = employeeID !== null && settings && settings[employeeID] ? settings[employeeID].color : null;
      } catch (e) {
        own = null;
      }
      if (typeof own === 'string' && /^#[0-9a-f]{6}$/i.test(own)) return own;
      return odColorToHex(card.provColor) || '';
    },

    /** Live procedures and note when the details have loaded, else the document's copy. */
    textFor(card) {
      const text = liveCardText(card, this.apptDetails);
      return { proc: text.proc, note: appointmentNote({ Note: text.note }) };
    },

    birthdayFor(card) {
      try {
        return !!this.isBirthday(card.patNum);
      } catch (e) {
        return false;
      }
    },

    // ── moves ───────────────────────────────────────────────────────────────────────────────
    /** A card is in the air: hold back snapshot updates, as the classic onStart does. */
    onDragBegin() {
      window.__isDragging = true;
      if (this.releaseTimer) clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    },

    /** The drag ended (a move, a refusal or a reorder): release the hold after the Firestore round trip, as onEnd does. */
    onDragFinish() {
      this.releaseSoon();
    },

    releaseSoon() {
      if (this.releaseTimer) clearTimeout(this.releaseTimer);
      this.releaseTimer = setTimeout(() => {
        this.releaseTimer = null;
        window.__isDragging = false;
        this.pendingDragOperation = null;
      }, DRAG_RELEASE_MS);
    },

    /** The raw card in the classic `pp.ops` list and its index (the board card is a copy). */
    findRaw(listId, card) {
      const list = this.pp && this.pp.ops ? this.pp.ops[listId] : null;
      if (!Array.isArray(list)) return { list: null, index: -1 };
      const index = list.findIndex(p => p && p.patNum === card.patNum && (card.aptNum === null || p.aptNum === card.aptNum));
      return { list, index };
    },

    /**
     * Commit one move the board decided on ({ card, from, to, kind }). The card is moved in the classic `pp.ops` first —
     * what draggable (or handleMoveFromCheckin) does — then moveCard does the writes: a seat → the board + In-Treatment in
     * Open Dental, a check-out → the Check-out panel (nothing is written until its Save), back to Check-in → the board only.
     */
    async commitMove({ card, from, to, kind }) {
      const { list, index } = this.findRaw(from, card);
      if (!list || index < 0) return;
      const raw = list[index];

      window.__isDragging = true;
      this.pendingDragOperation = { patNum: raw.patNum, from, to };
      list.splice(index, 1);
      if (!Array.isArray(this.pp.ops[to])) Vue.set(this.pp.ops, to, []);
      this.pp.ops[to].push(raw);

      try {
        await this.moveCard(raw, from, to);
        const who = cardLastFirst(card);
        if (kind === 'seat') {
          const room = this.board.labels[to] || 'the room';
          this.toast('success', `${who} seated in ${room}. Saved to Open Dental.`);
        } else if (kind === 'checkin') {
          this.toast('success', `${who} moved back to Check-in`);
        }
      } catch (err) {
        console.error('[practice-ops] move failed:', err);
      } finally {
        this.releaseSoon();
      }
    },

    /**
     * A move asked for by a view that does not decide for itself (the Operatory's Seat next / Check out / drop, the Phone's Move-to
     * sheet): the SAME decision the Board makes (moves.js planMove) — a reorder is nothing, a seat into a room that holds someone else
     * is refused with the toast — then the one commit path.
     */
    requestMove({ card, to } = {}) {
      const plan = planMove({ card, to, rooms: this.board.rooms });
      if (!plan) return;
      if (plan.refused) {
        this.toast('error', plan.message);
        return;
      }
      this.commitMove({
        card, from: plan.from, to: plan.to, kind: plan.kind,
      });
    },

    /** ⋯ → Dismiss: the classic dismiss(), which works on a card already in Check-out. */
    dismissCard(card) {
      const { list, index } = this.findRaw('checkout', card);
      if (!list || index < 0) return;
      this.dismiss(list[index], index);
      this.toast('success', `${cardLastFirst(card)} dismissed`);
    },

    toast(kind, message) {
      if (this.$toast && typeof this.$toast[kind] === 'function') this.$toast[kind](message);
    },

    // ── pings ───────────────────────────────────────────────────────────────────────────────
    /** Open Send alert for a patient's room (the room they are in, else the one they left), or for the last room used. */
    openPing(card) {
      const rooms = this.board.rooms;
      const own = card && (card.opNum || card.lastRoom);
      const fallback = this.pingRoom || (rooms[0] ? rooms[0].opNum : null);
      this.openPingFor(own || fallback);
    },

    /** Open Send alert for one room (the Operatory view's own room, when nobody is in it). */
    openPingFor(opNum) {
      this.pingRoom = opNum;
      this.applyPingRoom(opNum);
      this.showModal('ping');
    },

    /** The inherited startPing reads these two. The label is the room's Practice Ops name. */
    applyPingRoom(opNum) {
      const room = this.board.rooms.find(r => String(r.opNum) === String(opNum));
      this.selectedOpID = opNum === null || opNum === undefined || opNum === '' ? null : Number(opNum);
      this.selectedOpName = room ? room.label : (this.selectedOpName || '');
    },

    /** Clear one ping — the classic _deleteAlert, by the ping's index in the document's alerts[]. */
    clearPing(ping) {
      const alert = this.pp && Array.isArray(this.pp.alerts) ? this.pp.alerts[ping.index] : null;
      if (alert) this._deleteAlert(alert, ping.index);
    },

    // ── the visit checklist ─────────────────────────────────────────────────────────────────
    openChecklist({ card, key, el }) {
      if (!card || card.aptNum === null || card.aptNum === undefined) return;
      const rect = el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
      this.checklist = {
        aptNum: card.aptNum,
        patNum: card.patNum,
        anchor: rect ? {
          top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height,
        } : null,
        focus: ALERT_CATEGORY[key] || null,
      };
    },

    closeChecklist() {
      this.checklist = null;
    },

    /** A tick: the store's own action (optimistic, main-service /schedule-view-check) — exactly what ChecklistLayer does. */
    async onChecklistToggle({ itemKey, checked } = {}) {
      const open = this.checklist;
      if (!open || !itemKey) return;
      const result = await this.$store.dispatch('scheduleView/toggleCheck', { aptNum: open.aptNum, itemKey, checked: !!checked });
      if (result && result.ok === false) this.toast('error', result.error || 'Could not save the checkmark.');
    },

    /** "Open patient profile" / a category arrow: the patient's profile in a new tab, so the board stays up. */
    onChecklistOpenTab() {
      const open = this.checklist;
      this.closeChecklist();
      if (!open || !this.$router) return;
      const target = this.$router.resolve({ name: 'patientprofile', params: { patientID: `${this.siteID}-${open.patNum}` } });
      if (target && target.href) window.open(target.href, '_blank');
    },

    // ── alerts (Schedule View's) ────────────────────────────────────────────────────────────
    /** Fetch the practice's today. The store matches the payload to its `date`, so the date is set first. */
    loadAlerts() {
      if (this.isGone || !this.siteID || !this.todayYmd || !this.svState) return;
      this.alertsRequested = true;
      this.$store.commit('scheduleView/setDate', this.todayYmd);
      this.$store.dispatch('scheduleView/fetchAlerts', { date: this.todayYmd });
    },

    /** One refresh for a burst of changes. */
    queueAlertRefresh() {
      if (this.isGone) return;
      if (this.alertTimer) clearTimeout(this.alertTimer);
      this.alertTimer = setTimeout(() => {
        this.alertTimer = null;
        this.loadAlerts();
      }, ALERT_DEBOUNCE_MS);
    },

    /** Follow the patients on the board with the store's live `task` listener — only while icons are on and the office's `task` switch is. */
    syncTaskWatch() {
      if (this.isGone || !this.$store || !this.svState) return;
      const wanted = this.alertState.showIcons && alertSwitchOn(this.viewSettings, 'task');
      const patNums = boardPatNums(this.board);
      if (wanted && patNums.length) {
        this.$store.dispatch('scheduleView/watchTasks', patNums);
        this.taskWatching = true;
      } else if (this.taskWatching) {
        this.$store.dispatch('scheduleView/unwatchTasks');
        this.taskWatching = false;
      }
    },

    // ── live note / procedures ──────────────────────────────────────────────────────────────
    adoptApptBaseline() {
      const map = this.practicePulse && this.practicePulse.apptChanged;
      this.apptBaseline = map && typeof map === 'object' ? map : {};
    },

    /** Ask for the AptNums that are on the board and not loaded yet (on load: all of them, in one request). */
    syncApptDetails() {
      if (this.isGone || !this.siteID) return Promise.resolve();
      const nums = missingAptNums(this.board, this.apptDetails, this.detailsAsked);
      if (!nums.length) return Promise.resolve();
      nums.forEach((n) => { this.detailsAsked[n] = true; });
      return this.fetchApptDetails(nums);
    },

    async fetchApptDetails(nums) {
      const chunks = chunkAptNums(nums);
      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];
        // eslint-disable-next-line no-await-in-loop
        const reply = await this.getApptDetails({ siteID: this.siteID, aptNums: chunk });
        if (this.isGone) return;
        const rows = detailsFromReply(reply);
        if (Object.keys(rows).length === 0) {
          // an empty answer is a failed fetch (the store resolves [] on an error): let the next change ask again
          chunk.forEach((n) => { delete this.detailsAsked[n]; });
        } else {
          this.apptDetails = Object.assign({}, this.apptDetails, rows);
        }
      }
    },

    /** The AptNums an `apptChanged` signal named: ONE refetch for the burst. */
    queueApptRefetch(nums) {
      nums.forEach((n) => { this.pendingApptNums[n] = true; });
      if (this.apptTimer) clearTimeout(this.apptTimer);
      this.apptTimer = setTimeout(() => {
        this.apptTimer = null;
        const due = Object.keys(this.pendingApptNums).map(Number);
        this.pendingApptNums = {};
        if (due.length && !this.isGone) this.fetchApptDetails(due);
      }, DETAILS_DEBOUNCE_MS);
    },
  },
};
</script>

<style lang="scss" scoped>
.practice-ops {
  padding: 0 4px 24px;
}

// The Wall owns the whole screen (Layout.vue hid the shell): no page padding, no gutter.
.practice-ops--wall {
  padding: 0;
}

// The Operatory is full screen too (the shell is hidden), but it is a page of controls: a little air round it, none under it.
.practice-ops--operatory {
  padding: 14px 20px 20px;
}

// ── the title row (Board, Phone): the title and its date on the left, the picker + Report + Settings on the right ──
.ops-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
  margin-bottom: 8px;
}

.ops-head__titles {
  flex: 1;
  min-width: 220px;
}

.practice-ops__title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ops-head__sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  color: #707876;
}

.ops-head__live {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #0f7259;
}

.ops-head__tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.ops-head__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
  background: #ffffff;
  color: #262b2a;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  svg { display: block; flex: none; }
  &:hover { background: #f7faf9; }
  &:focus-visible { outline: 2px solid #4fa98d; outline-offset: 2px; }
}

.ops-head__btn--icon {
  width: 36px;
  padding: 0;
}

// the phone: the title and three icon buttons on ONE row, nothing else (design "Phone — Check-in")
.ops-head--phone {
  flex-wrap: nowrap;
  gap: 6px;

  .ops-head__titles { min-width: 0; }
  .practice-ops__title { font-size: 22px; }
  .ops-head__tools { flex-wrap: nowrap; gap: 6px; }
}

// The way back to the classic page: one small muted line under the title row, at the left.
.practice-ops__toggle {
  justify-content: flex-start;
  margin-bottom: 12px;
}

// on a phone it is one line: the note gives way (ellipsis), the link stays
.practice-ops__toggle--phone {
  flex-wrap: nowrap;
  margin-bottom: 4px;

  ::v-deep .fu-uitoggle__note {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 11.5px;
  }

  ::v-deep .fu-uitoggle__btn {
    flex: none;
    white-space: nowrap;
    font-size: 11.5px;
  }
}

.ops-note {
  margin-bottom: 10px;
  font-size: 12.5px;
  color: #707876;
}

.ops-state {
  max-width: 520px;
  margin: 40px auto;
  padding: 24px;
  text-align: center;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  background: #ffffff;
}

.ops-state__title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
}

.ops-state__text {
  margin: 0 0 14px;
  color: #3a4240;
}

.ops-state__btn {
  height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: #0f7259;
  color: #ffffff;
  font-family: inherit;
  font-weight: 700;
  cursor: pointer;
}

.ops-chat {
  margin-top: 24px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: #f9fbfd;
  border-radius: 8px;
  overflow: visible;
}

.ops-send {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ops-send__label {
  margin: 8px 0 0;
  font-size: 12.5px;
  font-weight: 700;
  color: #3a4240;
}

.ops-send__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.ops-send__or {
  margin: 8px 0 0;
  font-weight: 700;
}
</style>
